import { useMutation } from '@apollo/client';
import useReactRouter from "use-react-router";
import { faBoxOpen, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { Breadcrumb, Button, ButtonGroup, Form, Spinner, Table } from 'react-bootstrap';
import NumberFormat from 'react-number-format';
import * as XLSX from 'xlsx/xlsx.mjs';
import * as _ from 'lodash'
import { COLOR_APP } from '../../../constants';
import { CREATE_MANY_STOCKS, CREATE_STOCK } from '../../../graphql/stock/mutation';
import BasicModal from '../../../components/BasicModal';
import Illustration from "../../../image/Illustration.png";
import { useToasts } from 'react-toast-notifications';
import DownloadExcelButton from './DownloadExcelButton';
import useWindowDimensions from '../../../helpers/useWindowDimensions';
import LoadingSpinner from '../../../components/LoadingSpinner';

function ProductExcelAdd() {
    const { history } = useReactRouter();
    const { addToast } = useToasts();
    const { height, width } = useWindowDimensions();

    const [stockDatas, setStockDatas] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);
    const [selectedStock, setSelectedStock] = useState();
    const [showDeleteLiveConfirm, setShowDeleteLiveConfirm] = useState(false);

    const [createManyStocks, { loading: createManyStocksLoading }] = useMutation(CREATE_MANY_STOCKS);
    const totalImportAmount = React.useMemo(() => {
        let sum = 0;
        for (let s of stockDatas) {
            const raw = parseFloat((s?.amount ?? 0).toString().replace(/,/g, ''));
            sum += isNaN(raw) ? 0 : raw;
        }
        return sum;
    }, [stockDatas]);
    const handleFileUpload = (event) => {
        try {
        setError(false);
        setIsLoading(true);
    
        const file = event.target.files[0];
        if (!isValidFileType(file)) {
            setError(true); //  'Invalid file type. Only .xlsx, .xlsm, .xlsb, .xltx, and .xls files are supported.'
            setIsLoading(false);
            return;
        }
    
        const reader = new FileReader();
        reader.onload = async (e) => {
            const fileData = e.target.result;
            const workbook = XLSX.read(fileData, { type: 'binary' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            // console.log("object: ", jsonData.length, jsonData)
            
            const _newStocks = jsonData.slice(1)
            .filter(row => !_.isEmpty(row[0]?.toString()) && !_.isEmpty(row[1]?.toString()) 
                && !_.isEmpty(row[2]?.toString()) && !_.isEmpty(row[4]?.toString()) 
                && !_.isEmpty(row[5]?.toString()) && !_.isEmpty(row[6]?.toString()))
            .map((row, index) => ({
                id: index,
                cfMessage: getString(row[0]),
                name: getString(row[1]),
                unit: getString(row[2]),
                amount: getNumber(row[3], 1),
                originPrice: getNumber(row[4], 1),
                price: getNumber(row[5], 1),
                currency: getString(row[6]),
                image: ''
            }));
    
            setStockDatas(_newStocks);
            setIsLoading(false);
        };
    
        reader.readAsBinaryString(file);
        } catch (error) {
            setError(true);
            setIsLoading(false);
            console.error("Error reading Excel file:", error);
        }
    };
    
    const isValidFileType = (file) => {
        return ['xlsx', 'xlsm', 'xlsb', 'xltx', 'xls'].some((ext) => file.name.endsWith(ext));
    };

    const getString = (value) => value?.toString() ?? '';
    const getNumber = (value, fallback) => (value === undefined || value === null) ? fallback : Number(value);

    const _onCreateManyStock = async () => {
        try {
            if (createManyStocksLoading) return;
            let stocks = [];
            // TODO: Get shop id
            const shopId = await localStorage.getItem("SHOP");
            for (let stock of stockDatas) {
                const _newStock = {
                    cfMessage: stock?.cfMessage,
                    name: stock?.name,
                    unit: stock?.unit,
                    amount: parseFloat(stock?.amount.toString().replace(/,/g, "")) ?? 1,
                    amountAll: parseFloat(stock?.amount.toString().replace(/,/g, "")) ?? 1,
                    originPrice: parseFloat(stock?.originPrice.toString().replace(/,/g, "")) ?? 1,
                    price: parseFloat(stock?.price.toString().replace(/,/g, "")) ?? 1,
                    currency: stock?.currency,
                    shop: shopId,
                    image: ''
                }
                stocks.push(_newStock)
            }
            await createManyStocks({
                variables: {
                    data: {
                        stocks
                    },
                    where: {
                        shop: shopId
                    }
                }
            });

            addToast("ເພີ່ມສິນຄ້າເລັດແລ້ວ!", {
                appearance: "success",
                autoDismiss: true,
            });
            history.goBack();
        } catch (error) {
            console.log(error)
        }
    }

    return (
        <div style={{paddingBottom:'4em'}} >
            <div style={{ height: 20 }} />
            <Breadcrumb style={{ marginBottom: 0 }} >
                <Breadcrumb.Item style={{ fontSize: 14, color: COLOR_APP }} onClick={() => history.goBack()}>
                    <FontAwesomeIcon icon={faBoxOpen} style={{ color: COLOR_APP }} /><span style={{ color: COLOR_APP }}>ຄັງສິນຄ້າ</span></Breadcrumb.Item>
                <Breadcrumb.Item style={{ fontSize: 14 }} active>ເພີ່ມສິນຄ້າດ້ວຍ Excel</Breadcrumb.Item>
            </Breadcrumb>
            <div style={{ margin: '0px 5px' }}>
                <div style={{ fontWeight: 'bold' }}>ເລືອກ File Excel ຂອງທ່ານ</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                    <input className='form-control' style={{ width: '70vh' }} type="file" onChange={handleFileUpload} />
                    {width > 700 && <DownloadExcelButton />}
                </div>
                {width <= 700 && <DownloadExcelButton />}
            </div>
            {
                error && <div> Error to import file</div>
            }
            <div style={{padding:'10px 0'}}>
                <p><b>ຂໍ້ຄວນປະຕິບັດ:</b> ຂໍ້ມູນທີ່ຈຳເປັນຕ້ອງໄດ້ປ້ອນມີດັ່ງນີ້: "ລະຫັດ CF", "ຊື່ສິນຄ້າ", "ຫົວຫນວ່ຍ", "ຈຳນວນ", "ລາຄາຕົ້ນທືນ(ຫ້າມໃສ່ ເຄື່ອງໝາຍຈ້ຳ ແລະ ຈຸດ)", "ລາຄາຂາຍ(ຫ້າມໃສ່ ເຄື່ອງໝາຍຈ້ຳ ແລະ ຈຸດ)", "ສະກຸນເງິນ". ກະລຸນາກວດສອບເບິ່ງຟາຍ excel ຂອງທ່ານໃຫ້ຖຶກຕ້ອງ ຂໍຂອບໃຈ</p>
            </div>
            {isLoading && <LoadingSpinner />}
            {
                (stockDatas?.length > 0 && !isLoading) && <Table responsive className="mt-4">
                    <thead>
                        <tr style={{ whiteSpace: 'nowrap' }}>
                            <th style={{ borderBottom: 'none' }}>#</th>
                            <th style={{ borderBottom: 'none' }}>ລະຫັດ CF</th>
                            <th style={{ borderBottom: 'none' }}>ຊື່ສິນຄ້າ</th>
                            <th style={{ borderBottom: 'none' }}>ຫົວຫນວ່ຍ</th>
                            <th style={{ borderBottom: 'none' }}>ຈໍານວນ</th>
                            <th style={{ borderBottom: 'none' }}>ລາຄາຕົ້ນທືນ</th>
                            <th style={{ borderBottom: 'none' }}>ລາຄາຂາຍ</th>
                            <th style={{ borderBottom: 'none' }}>ສະກຸນເງິນ</th>
                            <th style={{ borderBottom: 'none' }}>ຈັດການ</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            stockDatas?.length > 0 && stockDatas?.map((stock, index) =>
                            (<tr key={index} style={{ whiteSpace: 'nowrap' }}>
                                <td>{index + 1}</td>
                                <td>
                                    <Form.Control type='input' value={stock?.cfMessage}
                                        onChange={(e) => {
                                            const _index = _.findIndex(stockDatas, { id: stock?.id });
                                            let _newStocks = [...stockDatas]
                                            _newStocks[_index].cfMessage = e.target.value;
                                            setStockDatas(_newStocks)
                                        }}
                                    />
                                </td>
                                <td>
                                    <Form.Control type='input' value={stock?.name}
                                        onChange={(e) => {
                                            const _index = _.findIndex(stockDatas, { id: stock?.id });
                                            let _newStocks = [...stockDatas]
                                            _newStocks[_index].name = e.target.value;
                                            setStockDatas(_newStocks)
                                        }}
                                    />
                                </td>
                                <td>
                                    <Form.Control type='input' value={stock?.unit}
                                        onChange={(e) => {
                                            const _index = _.findIndex(stockDatas, { id: stock?.id });
                                            let _newStocks = [...stockDatas]
                                            _newStocks[_index].unit = e.target.value;
                                            setStockDatas(_newStocks)
                                        }}
                                    />
                                </td>
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <NumberFormat
                                            name="amount"
                                            onChange={(e) => {
                                                const _index = _.findIndex(stockDatas, { id: stock?.id });
                                                let _newStocks = [...stockDatas];
                                                _newStocks[_index].amount = e.target.value;
                                                setStockDatas(_newStocks);
                                            }}
                                            value={stock?.amount}
                                            className="form-control"
                                            placeholder="0"
                                            thousandSeparator={true}
                                            prefix={''} />
                                        <ButtonGroup aria-label="Adjust amount">
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() => {
                                                    const _index = _.findIndex(stockDatas, { id: stock?.id });
                                                    let _newStocks = [...stockDatas];
                                                    const current = parseFloat((_newStocks[_index].amount ?? 0).toString().replace(/,/g, '')) || 0;
                                                    _newStocks[_index].amount = Math.max(0, current - 1);
                                                    setStockDatas(_newStocks);
                                                }}
                                                style={{ minWidth: 36 }}
                                            >-</Button>
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() => {
                                                    const _index = _.findIndex(stockDatas, { id: stock?.id });
                                                    let _newStocks = [...stockDatas];
                                                    const current = parseFloat((_newStocks[_index].amount ?? 0).toString().replace(/,/g, '')) || 0;
                                                    _newStocks[_index].amount = current + 1;
                                                    setStockDatas(_newStocks);
                                                }}
                                                style={{ minWidth: 36 }}
                                            >+</Button>
                                        </ButtonGroup>
                                    </div>
                                </td>
                                <td>
                                    <NumberFormat
                                        name="originPrice"
                                        onChange={(e) => {
                                            const _index = _.findIndex(stockDatas, { id: stock?.id });
                                            let _newStocks = [...stockDatas]
                                            _newStocks[_index].originPrice = e.target.value;
                                            setStockDatas(_newStocks)
                                        }}
                                        value={stock?.originPrice}
                                        className="form-control"
                                        placeholder="0"
                                        thousandSeparator={true}
                                        prefix={''} />
                                </td>
                                <td>
                                        <NumberFormat
                                            name="price"
                                            onChange={(e) => {
                                                const _index = _.findIndex(stockDatas, { id: stock?.id });
                                                let _newStocks = [...stockDatas]
                                                _newStocks[_index].price = e.target.value;
                                                setStockDatas(_newStocks)
                                            }}
                                            value={stock?.price}
                                            className="form-control"
                                            placeholder="0"
                                            thousandSeparator={true}
                                            prefix={''} />
                                </td>
                                <td>
                                    <ButtonGroup aria-label="Basic example">
                                        <Button
                                            style={{
                                                background:
                                                    stock?.currency == "ກີບ" ? COLOR_APP : "#eee",
                                                color: stock?.currency == "ກີບ" ? "#ffff" : "#000",
                                                width: 50,
                                                borderWidth: 0,
                                            }}
                                            onClick={(e) => {
                                                const _index = _.findIndex(stockDatas, { id: stock?.id });
                                                let _newStocks = [...stockDatas]
                                                _newStocks[_index].currency = "ກີບ"
                                                setStockDatas(_newStocks)
                                            }}
                                        >
                                            ກີບ
                                        </Button>
                                        <Button
                                            style={{
                                                background:
                                                    stock?.currency == "ບາດ" ? COLOR_APP : "#eee",
                                                color: stock?.currency == "ບາດ" ? "#ffff" : "#000",
                                                width: 50,
                                                marginLeft: 2,
                                                borderWidth: 0,
                                            }}
                                            onClick={(e) => {
                                                const _index = _.findIndex(stockDatas, { id: stock?.id });
                                                let _newStocks = [...stockDatas]
                                                _newStocks[_index].currency = "ບາດ"
                                                setStockDatas(_newStocks)
                                            }}
                                        >
                                            ບາດ
                                        </Button>
                                        <Button
                                            style={{
                                                background:
                                                    stock?.currency == "ໂດລາ" ? COLOR_APP : "#eee",
                                                color: stock?.currency == "ໂດລາ" ? "#ffff" : "#000",
                                                width: 50,
                                                marginLeft: 2,
                                                borderWidth: 0,
                                            }}
                                            onClick={(e) => {
                                                const _index = _.findIndex(stockDatas, { id: stock?.id });
                                                let _newStocks = [...stockDatas]
                                                _newStocks[_index].currency = "ໂດລາ"
                                                setStockDatas(_newStocks)
                                            }}
                                        >
                                            ໂດລາ
                                        </Button>
                                    </ButtonGroup>
                                </td>
                             
                                <td>
                                    <button
                                        className="btn hoverme"
                                        onClick={() => {
                                            setShowDeleteLiveConfirm(true);
                                            setSelectedStock(stock);
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faTrashAlt}
                                            style={{ color: "red" }}
                                        />
                                    </button>
                                </td>
                            </tr>)
                            )
                        }
                    </tbody>
                </Table>
            }

            {/* {
                stockDatas?.length > 0
                &&  */}
                <div style={{
                    position:"fixed",width:'100%',right:0,bottom:0,zIndex:10,display:'flex',gap:5, justifyContent:'end',padding:10,background:'#ffffff'
                }}>
                    <div style={{ marginRight: 'auto', alignSelf: 'center', fontWeight: 'bold' }}>
                        ຈຳນວນນຳເຂົ້າ: {totalImportAmount}
                    </div>
                    <Button variant='outline-secondary' style={{minWidth:200}} onClick={() => setStockDatas([])}>ຍົກເລີກ</Button>
                    <Button
                        style={{
                            background: COLOR_APP,
                            border: 'none',
                            minWidth:200
                        }}
                        onClick={_onCreateManyStock}>
                        {createManyStocksLoading ? <Spinner size="sm" animation="border" /> : "ບັນທືກ"}
                    </Button>
                </div>
            {/* } */}

            <BasicModal
                show={showDeleteLiveConfirm}
                onHide={() => setShowDeleteLiveConfirm(false)}
                okay={() => {
                    let _newStocks = [...stockDatas]
                    _.remove(_newStocks, { id: selectedStock?.id });
                    setStockDatas(_newStocks);
                    setShowDeleteLiveConfirm(false);
                    setSelectedStock();
                    addToast("ລຶບຂໍ້ມູນສໍາເລັດແລ້ວ!", {
                        appearance: "success",
                        autoDismiss: true,
                    });
                }}
                title="ທ່ານຕ້ອງການລຶບຂໍ້ມູນ ຫຼື ບໍ່"
                leftButtonName="ຍົກເລີກ"
                rightButtonName="ຕົກລົງ"
                Illustration={Illustration} />
        </div>
    );
}

export default ProductExcelAdd;
